// Måneposisjon, månefase og månelys for NVG-planlegging.
//
// ── ALGORITME OG NØYAKTIGHET ────────────────────────────────────────────────
// Kilde: J. Meeus, «Astronomical Algorithms», 2. utg. (1998):
//   * kap. 47 «Position of the Moon» — trunkert ELP-2000/82-serie, tabell 47.A
//     (60 ledd for lengde og radiusvektor) og 47.B (60 ledd for bredde), med de
//     additive A1/A2/A3-leddene og eksentrisitetsfaktoren E. Meeus oppgir
//     nøyaktighet ~10" i lengde og ~4" i bredde — dvs. ca. 0.003°, som er
//     langt bedre enn nødvendig for lysberegning.
//   * kap. 48 «Illuminated Fraction of the Moon's Disk» — fasevinkel via
//     elongasjon og avstander, k = (1 + cos i)/2.
//   * kap. 15 for opp-/nedgangskriteriet: månens øvre rand tangerer horisonten,
//     dvs. h0 = 0.7275·π − 34' der π er horisontalparallaksen. Vi regner
//     topocentrisk høyde direkte og legger til halvdiameter + refraksjon.
//
// Praktiske grenser:
//   * Topocentrisk korreksjon er 1. ordens (Δh = −π·cos h). Den ignorerer
//     jordas flattrykning og observatørens høyde over havet — feil < 1',
//     forsømbar mot refraksjonsusikkerheten ved horisonten.
//   * Måne opp/ned har derfor samme fysiske usikkerhet som sol opp/ned:
//     noen minutter, dominert av faktisk refraksjon nær horisonten.
//   * MÅNELYS-MODELLEN er en fotometrisk MODELL, ikke en måling. Se
//     `nvgAssessment` for hva som er inne og hva som ikke er.
//
// `@skade/contracts` `astro.ts` har en enklere `moonIllumination()` (én-ledds
// SunCalc-serie, ~0.5° posisjonsfeil, ingen måneposisjon/oppgang). Denne modulen
// er den presise varianten; `astro.ts` er urørt, og testene krysssjekker mot den.
import { apparentSiderealTimeDeg, equatorialToHorizontal, findThresholdCrossings, julianDay, julianEphemerisDay, lightPhaseAt, normalizeDeg, normalizeDeg180, refractionDeg, sunApparentEquatorial, sunPosition, } from './sun.js';
const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;
const DAY_MS = 86_400_000;
/** Astronomisk enhet i km (IAU 2012). */
const AU_KM = 149_597_870.7;
/** Jordas ekvatorradius i km — brukt i parallakse-formelen (Meeus 47.). */
const EARTH_EQ_RADIUS_KM = 6378.14;
/** Synodisk måned (nymåne → nymåne) i døgn — middelverdi. */
export const SYNODIC_MONTH_DAYS = 29.530588853;
// ── Meeus tabell 47.A: [D, M, M', F, Σl (1e-6 °), Σr (1e-3 km)] ─────────────
const TERMS_LR = [
    [0, 0, 1, 0, 6288774, -20905355],
    [2, 0, -1, 0, 1274027, -3699111],
    [2, 0, 0, 0, 658314, -2955968],
    [0, 0, 2, 0, 213618, -569925],
    [0, 1, 0, 0, -185116, 48888],
    [0, 0, 0, 2, -114332, -3149],
    [2, 0, -2, 0, 58793, 246158],
    [2, -1, -1, 0, 57066, -152138],
    [2, 0, 1, 0, 53322, -170733],
    [2, -1, 0, 0, 45758, -204586],
    [0, 1, -1, 0, -40923, -129620],
    [1, 0, 0, 0, -34720, 108743],
    [0, 1, 1, 0, -30383, 104755],
    [2, 0, 0, -2, 15327, 10321],
    [0, 0, 1, 2, -12528, 0],
    [0, 0, 1, -2, 10980, 79661],
    [4, 0, -1, 0, 10675, -34782],
    [0, 0, 3, 0, 10034, -23210],
    [4, 0, -2, 0, 8548, -21636],
    [2, 1, -1, 0, -7888, 24208],
    [2, 1, 0, 0, -6766, 30824],
    [1, 0, -1, 0, -5163, -8379],
    [1, 1, 0, 0, 4987, -16675],
    [2, -1, 1, 0, 4036, -12831],
    [2, 0, 2, 0, 3994, -10445],
    [4, 0, 0, 0, 3861, -11650],
    [2, 0, -3, 0, 3665, 14403],
    [0, 1, -2, 0, -2689, -7003],
    [2, 0, -1, 2, -2602, 0],
    [2, -1, -2, 0, 2390, 10056],
    [1, 0, 1, 0, -2348, 6322],
    [2, -2, 0, 0, 2236, -9884],
    [0, 1, 2, 0, -2120, 5751],
    [0, 2, 0, 0, -2069, 0],
    [2, -2, -1, 0, 2048, -4950],
    [2, 0, 1, -2, -1773, 4130],
    [2, 0, 0, 2, -1595, 0],
    [4, -1, -1, 0, 1215, -3958],
    [0, 0, 2, 2, -1110, 0],
    [3, 0, -1, 0, -892, 3258],
    [2, 1, 1, 0, -810, 2616],
    [4, -1, -2, 0, 759, -1897],
    [0, 2, -1, 0, -713, -2117],
    [2, 2, -1, 0, -700, 2354],
    [2, 1, -2, 0, 691, 0],
    [2, -1, 0, -2, 596, 0],
    [4, 0, 1, 0, 549, -1423],
    [0, 0, 4, 0, 537, -1117],
    [4, -1, 0, 0, 520, -1571],
    [1, 0, -2, 0, -487, -1739],
    [2, 1, 0, -2, -399, 0],
    [0, 0, 2, -2, -381, -4421],
    [1, 1, 1, 0, 351, 0],
    [3, 0, -2, 0, -340, 0],
    [4, 0, -3, 0, 330, 0],
    [2, -1, 2, 0, 327, 0],
    [0, 2, 1, 0, -323, 1165],
    [1, 1, -1, 0, 299, 0],
    [2, 0, 3, 0, 294, 0],
    [2, 0, -1, -2, 0, 8752],
];
// ── Meeus tabell 47.B: [D, M, M', F, Σb (1e-6 °)] ───────────────────────────
const TERMS_B = [
    [0, 0, 0, 1, 5128122],
    [0, 0, 1, 1, 280602],
    [0, 0, 1, -1, 277693],
    [2, 0, 0, -1, 173237],
    [2, 0, -1, 1, 55413],
    [2, 0, -1, -1, 46271],
    [2, 0, 0, 1, 32573],
    [0, 0, 2, 1, 17198],
    [2, 0, 1, -1, 9266],
    [0, 0, 2, -1, 8822],
    [2, -1, 0, -1, 8216],
    [2, 0, -2, -1, 4324],
    [2, 0, 1, 1, 4200],
    [2, 1, 0, -1, -3359],
    [2, -1, -1, 1, 2463],
    [2, -1, 0, 1, 2211],
    [2, -1, -1, -1, 2065],
    [0, 1, -1, -1, -1870],
    [4, 0, -1, -1, 1828],
    [0, 1, 0, 1, -1794],
    [0, 0, 0, 3, -1749],
    [0, 1, -1, 1, -1565],
    [1, 0, 0, 1, -1491],
    [0, 1, 1, 1, -1475],
    [0, 1, 1, -1, -1410],
    [0, 1, 0, -1, -1344],
    [1, 0, 0, -1, -1335],
    [0, 0, 3, 1, 1107],
    [4, 0, 0, -1, 1021],
    [4, 0, -1, 1, 833],
    [0, 0, 1, -3, 777],
    [4, 0, -2, 1, 671],
    [2, 0, 0, -3, 607],
    [2, 0, 2, -1, 596],
    [2, -1, 1, -1, 491],
    [2, 0, -2, 1, -451],
    [0, 0, 3, -1, 439],
    [2, 0, 2, 1, 422],
    [2, 0, -3, -1, 421],
    [2, 1, -1, 1, -366],
    [2, 1, 0, 1, -351],
    [4, 0, 0, 1, 331],
    [2, -1, 1, 1, 315],
    [2, -2, 0, -1, 302],
    [0, 0, 1, 3, -283],
    [2, 1, 1, -1, -229],
    [1, 1, 0, -1, 223],
    [1, 1, 0, 1, 223],
    [0, 1, -2, -1, -220],
    [2, 1, -1, -1, -220],
    [1, 0, 1, 1, -185],
    [2, -1, -2, -1, 181],
    [0, 1, 2, 1, -177],
    [4, 0, -2, -1, 176],
    [4, -1, -1, -1, 166],
    [1, 0, 1, -1, -164],
    [4, 0, 1, -1, 132],
    [1, 0, -1, -1, -119],
    [4, -1, 0, -1, 115],
    [2, -2, 0, 1, 107],
];
/**
 * Månens geosentriske ekliptiske koordinater (Meeus kap. 47). `jde` er Julian
 * Ephemeris Day (TT). Eksponert fordi den verifiseres direkte mot Meeus'
 * eksempel 47.a i testene.
 */
export function moonEcliptic(jde) {
    const T = (jde - 2451545) / 36525;
    const T2 = T * T;
    const T3 = T2 * T;
    const T4 = T3 * T;
    // Månens middellengde L' (47.1).
    const Lp = 218.3164477 + 481267.88123421 * T - 0.0015786 * T2 + T3 / 538841 - T4 / 65194000;
    // Middelelongasjon D (47.2).
    const D = 297.8501921 + 445267.1114034 * T - 0.0018819 * T2 + T3 / 545868 - T4 / 113065000;
    // Solas middelanomali M (47.3).
    const M = 357.5291092 + 35999.0502909 * T - 0.0001536 * T2 + T3 / 24490000;
    // Månens middelanomali M' (47.4).
    const Mp = 134.9633964 + 477198.8675055 * T + 0.0087414 * T2 + T3 / 69699 - T4 / 14712000;
    // Månens breddeargument F (47.5).
    const F = 93.272095 + 483202.0175233 * T - 0.0036539 * T2 - T3 / 3526000 + T4 / 863310000;
    // Additive argumenter (Venus, Jupiter, jordas flattrykning).
    const A1 = normalizeDeg(119.75 + 131.849 * T) * DEG;
    const A2 = normalizeDeg(53.09 + 479264.29 * T) * DEG;
    const A3 = normalizeDeg(313.45 + 481266.484 * T) * DEG;
    // Eksentrisitets-faktor for ledd med solas middelanomali (47.6).
    const E = 1 - 0.002516 * T - 0.0000074 * T2;
    const Dr = normalizeDeg(D) * DEG;
    const Mr = normalizeDeg(M) * DEG;
    const Mpr = normalizeDeg(Mp) * DEG;
    const Fr = normalizeDeg(F) * DEG;
    const Lpr = normalizeDeg(Lp) * DEG;
    let sumL = 0;
    let sumR = 0;
    for (const [cd, cm, cmp, cf, cl, cr] of TERMS_LR) {
        const arg = cd * Dr + cm * Mr + cmp * Mpr + cf * Fr;
        const ecc = E ** Math.abs(cm);
        sumL += cl * ecc * Math.sin(arg);
        sumR += cr * ecc * Math.cos(arg);
    }
    let sumB = 0;
    for (const [cd, cm, cmp, cf, cb] of TERMS_B) {
        const arg = cd * Dr + cm * Mr + cmp * Mpr + cf * Fr;
        sumB += cb * E ** Math.abs(cm) * Math.sin(arg);
    }
    // Additive ledd (Meeus, rett etter tabell 47.B).
    sumL += 3958 * Math.sin(A1) + 1962 * Math.sin(Lpr - Fr) + 318 * Math.sin(A2);
    sumB +=
        -2235 * Math.sin(Lpr) +
            382 * Math.sin(A3) +
            175 * Math.sin(A1 - Fr) +
            175 * Math.sin(A1 + Fr) +
            127 * Math.sin(Lpr - Mpr) -
            115 * Math.sin(Lpr + Mpr);
    const longitudeDeg = normalizeDeg(Lp + sumL / 1e6);
    const latitudeDeg = sumB / 1e6;
    const distanceKm = 385000.56 + sumR / 1000;
    const parallaxDeg = Math.asin(EARTH_EQ_RADIUS_KM / distanceKm) * RAD;
    return { longitudeDeg, latitudeDeg, distanceKm, parallaxDeg };
}
/**
 * Nutasjon i lengde (grader) — kort serie (Meeus 22, hovedledd). Brukes bare til
 * å gjøre månens lengde tilsynelatende; feilen er < 0.5" og uten betydning her.
 */
function nutationLongitudeDeg(jde) {
    const T = (jde - 2451545) / 36525;
    const omega = (125.04452 - 1934.136261 * T) * DEG;
    const L = (280.4665 + 36000.7698 * T) * DEG;
    const Lp = (218.3165 + 481267.8813 * T) * DEG;
    const arcsec = -17.2 * Math.sin(omega) -
        1.32 * Math.sin(2 * L) -
        0.23 * Math.sin(2 * Lp) +
        0.21 * Math.sin(2 * omega);
    return arcsec / 3600;
}
/** Måneposisjon for et tidspunkt (UTC-ms) og en posisjon (lon østlig positiv). */
export function moonPosition(timeMs, lat, lon) {
    const jde = julianEphemerisDay(timeMs);
    const m = moonEcliptic(jde);
    const sun = sunApparentEquatorial(jde);
    const eps = sun.trueObliquityDeg * DEG;
    const lam = (m.longitudeDeg + nutationLongitudeDeg(jde)) * DEG;
    const beta = m.latitudeDeg * DEG;
    const rightAscensionDeg = normalizeDeg(Math.atan2(Math.sin(lam) * Math.cos(eps) - Math.tan(beta) * Math.sin(eps), Math.cos(lam)) * RAD);
    const declinationDeg = Math.asin(Math.sin(beta) * Math.cos(eps) + Math.cos(beta) * Math.sin(eps) * Math.sin(lam)) * RAD;
    const gst = apparentSiderealTimeDeg(julianDay(timeMs), sun.nutationLongitudeDeg, sun.trueObliquityDeg);
    const hourAngleDeg = normalizeDeg180(gst + lon - rightAscensionDeg);
    const hz = equatorialToHorizontal(hourAngleDeg, declinationDeg, lat);
    // Topocentrisk høyde, 1. ordens parallakse: Δh = −π·cos(h).
    const elevationDeg = hz.elevationDeg - m.parallaxDeg * Math.cos(hz.elevationDeg * DEG);
    return {
        azimuthDeg: hz.azimuthDeg,
        elevationDeg,
        apparentElevationDeg: elevationDeg + refractionDeg(elevationDeg),
        geocentricElevationDeg: hz.elevationDeg,
        rightAscensionDeg,
        declinationDeg,
        distanceKm: m.distanceKm,
        parallaxDeg: m.parallaxDeg,
        semidiameterDeg: 0.2725 * m.parallaxDeg,
    };
}
const PHASE_NAMES = [
    'Nymåne',
    'Tiltakende sigd',
    'Første kvarter',
    'Tiltakende, nesten full',
    'Fullmåne',
    'Avtakende, nesten full',
    'Siste kvarter',
    'Avtakende sigd',
];
/** Månefase og belyst andel for et tidspunkt (UTC-ms). Geosentrisk. */
export function moonPhase(timeMs) {
    const jde = julianEphemerisDay(timeMs);
    const m = moonEcliptic(jde);
    const s = sunApparentEquatorial(jde);
    const sunKm = s.distanceAu * AU_KM;
    // Elongasjon via ekvatorialkoordinater (Meeus 48.2).
    const eps = s.trueObliquityDeg * DEG;
    const lam = m.longitudeDeg * DEG;
    const beta = m.latitudeDeg * DEG;
    const moonRa = Math.atan2(Math.sin(lam) * Math.cos(eps) - Math.tan(beta) * Math.sin(eps), Math.cos(lam));
    const moonDec = Math.asin(Math.sin(beta) * Math.cos(eps) + Math.cos(beta) * Math.sin(eps) * Math.sin(lam));
    const sunRa = s.rightAscensionDeg * DEG;
    const sunDec = s.declinationDeg * DEG;
    const cosPsi = Math.sin(sunDec) * Math.sin(moonDec) +
        Math.cos(sunDec) * Math.cos(moonDec) * Math.cos(sunRa - moonRa);
    const psi = Math.acos(Math.max(-1, Math.min(1, cosPsi)));
    // Fasevinkel i (Meeus 48.3) — henter inn avstandene, ikke bare elongasjonen.
    const phaseAngle = Math.atan2(sunKm * Math.sin(psi), m.distanceKm - sunKm * Math.cos(psi));
    const illuminatedFraction = (1 + Math.cos(phaseAngle)) / 2;
    // Fase-brøken defineres som differansen i TILSYNELATENDE ekliptisk lengde
    // (samme definisjon USNO/IMCCE bruker for nymåne/fullmåne-øyeblikkene), så
    // månens lengde må også nutasjons-korrigeres.
    const phase = normalizeDeg(m.longitudeDeg + nutationLongitudeDeg(jde) - s.apparentLongitudeDeg) / 360;
    const idx = Math.round(phase * 8) % 8;
    return {
        illuminatedFraction,
        phaseAngleDeg: phaseAngle * RAD,
        elongationDeg: psi * RAD,
        phase,
        phaseName: PHASE_NAMES[idx],
        waxing: phase < 0.5,
        ageDays: phase * SYNODIC_MONTH_DAYS,
    };
}
/**
 * Neste tidspunkt etter `fromMs` der fasen passerer `targetPhase`
 * (0 = nymåne, 0.25 = første kvarter, 0.5 = fullmåne, 0.75 = siste kvarter).
 * Numerisk søk på lengde-differansen sol/måne. Nøyaktighet noen minutter —
 * bruk Meeus kap. 49 hvis du trenger sekunder.
 */
export function nextMoonPhaseMs(fromMs, targetPhase) {
    const target = normalizeDeg(targetPhase * 360);
    const f = (ms) => normalizeDeg180(moonPhase(ms).phase * 360 - target);
    const stepMs = 6 * 3_600_000;
    let prevT = fromMs;
    let prev = f(prevT);
    const limit = fromMs + 40 * DAY_MS;
    for (let t = fromMs + stepMs; t <= limit; t += stepMs) {
        const cur = f(t);
        // Nullgjennomgang nedenfra: fasen øker monotont, så bare −→+ er ekte.
        if (prev < 0 && cur >= 0) {
            let lo = prevT;
            let hi = t;
            for (let i = 0; i < 40 && hi - lo > 250; i++) {
                const mid = (lo + hi) / 2;
                if (f(mid) < 0)
                    lo = mid;
                else
                    hi = mid;
            }
            return Math.round((lo + hi) / 2);
        }
        prevT = t;
        prev = cur;
    }
    return null;
}
// ── Måne opp/ned ────────────────────────────────────────────────────────────
/**
 * Høyden til månens ØVRE RAND over den refraksjonskorrigerte horisonten
 * (grader). Nullgjennomgang = måne opp/ned, jf. Meeus kap. 15 (h0 = 0.7275·π
 * − 34'), her uttrykt topocentrisk: h_sentrum + halvdiameter + refraksjon.
 */
export function moonUpperLimbAltitudeDeg(timeMs, lat, lon) {
    const p = moonPosition(timeMs, lat, lon);
    return p.elevationDeg + p.semidiameterDeg + refractionDeg(p.elevationDeg);
}
/**
 * Måne opp/ned for UTC-KALENDERDØGNET som `timeMs` faller i (til forskjell fra
 * `sunDay`, som bruker soldøgnet rundt solmiddag).
 *
 * MERK: `moonsetMs` kan godt være FØR `moonriseMs` innenfor samme kalenderdøgn
 * — det er normalt, ikke en feil. Eksempel (verifisert mot USNO): Oslo
 * 2026-12-21 har månenedgang 05:21 og månoppgang 11:22 UTC.
 */
export function moonDay(timeMs, lat, lon) {
    const d = new Date(timeMs);
    const dayStart = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const dayEnd = dayStart + DAY_MS;
    const alt = (ms) => moonUpperLimbAltitudeDeg(ms, lat, lon);
    let maxAltitudeDeg = -Infinity;
    let minAltitudeDeg = Infinity;
    for (let t = dayStart; t <= dayEnd; t += 300_000) {
        const a = alt(t);
        if (a > maxAltitudeDeg)
            maxAltitudeDeg = a;
        if (a < minAltitudeDeg)
            minAltitudeDeg = a;
    }
    const cs = findThresholdCrossings(alt, 0, dayStart, dayEnd, 300_000);
    const rise = cs.find((c) => c.rising) ?? null;
    const setC = [...cs].reverse().find((c) => !c.rising) ?? null;
    let state;
    if (rise && setC)
        state = 'inntreffer';
    else if (rise || setC)
        state = 'delvis';
    else
        state = minAltitudeDeg > 0 ? 'alltid-over' : 'alltid-under';
    return {
        dateUtc: new Date(dayStart).toISOString().slice(0, 10),
        moonriseMs: rise ? rise.atMs : null,
        moonsetMs: setC ? setC.atMs : null,
        state,
        maxAltitudeDeg,
        minAltitudeDeg,
    };
}
// ── Månelys / NVG-vurdering ─────────────────────────────────────────────────
/** Horisontal belysning fra fullmåne i senit, klar himmel (millilux). */
const FULL_MOON_ZENITH_MLX = 250;
/**
 * Klarhimmels-«gulv»: stjernelys + airglow + (i Norge ofte) nordlys og
 * lysforurensning. 0.8 mlx klart / 0.3 mlx overskyet er de vanlig oppgitte
 * verdiene. Dette er grunnen til at NVG virker på månefri natt i det hele tatt.
 */
const AMBIENT_CLEAR_MLX = 0.8;
const AMBIENT_OVERCAST_MLX = 0.3;
/** Visuell ekstinksjonskoeffisient (mag/luftmasse), klar luft ved havnivå. */
const EXTINCTION_MAG_PER_AIRMASS = 0.28;
/**
 * Månens lysstyrke relativt fullmåne som funksjon av fasevinkel.
 * Krisciunas & Schaefer (1991): Δm = 0.026·|α| + 4·10⁻⁹·α⁴ magnituder.
 * Sterkt IKKE-lineært: halvmåne (α = 90°) gir bare ~9 % av fullmånens lys,
 * ikke 50 %. Dette er den nest vanligste feilen i månelys-planlegging.
 */
export function moonPhaseBrightnessFactor(phaseAngleDeg) {
    const a = Math.min(Math.abs(phaseAngleDeg), 180);
    const dMag = 0.026 * a + 4e-9 * a ** 4;
    return 10 ** (-0.4 * dMag);
}
/** Luftmasse (Kasten & Young 1989). h = tilsynelatende høyde i grader. */
export function airmass(apparentElevationDeg) {
    const h = Math.max(apparentElevationDeg, 0);
    return 1 / (Math.sin(h * DEG) + 0.50572 * (h + 6.07995) ** -1.6364);
}
/**
 * Klassifisering av total horisontal belysning for Gen-3 bildeforsterker,
 * snøfritt terreng. Tommelfingerregler, med ankerpunktene:
 *   0.3 mlx  overskyet, månefri natt   → 'ingen'     (NVG på ytelsesgrensen)
 *   0.8 mlx  klar, månefri natt        → 'marginal'  (stjernelys alene)
 *   ~10 mlx  halvmåne middels høyt     → 'brukbar'
 *   ~50 mlx  fullmåne middels høyt     → 'god'
 *   >80 mlx  fullmåne høyt, klart      → 'utmerket'
 */
function classify(totalMilliLux) {
    if (totalMilliLux < 0.5)
        return 'ingen';
    if (totalMilliLux < 5)
        return 'marginal';
    if (totalMilliLux < 20)
        return 'brukbar';
    if (totalMilliLux < 80)
        return 'god';
    return 'utmerket';
}
/**
 * Operativ månelys-/NVG-vurdering for et tidspunkt og en posisjon.
 *
 * MODELLEN (all ren geometri + fotometri, ingen måling):
 *   E = 250 mlx · f(α) · sin(h) · T_atm(h) · T_sky(skydekke)
 *   f(α)     = Krisciunas & Schaefer fasefunksjon (se over).
 *   sin(h)   = projeksjon på horisontal flate; h = månens elevasjon.
 *   T_atm    = 10^(−0.4·k·(X−1)), k = 0.28 mag/luftmasse, X = Kasten & Young.
 *   T_sky    = 10^(−2·skydekke²) — GROV plassholder.
 *
 * DETTE ER IKKE HELE SANNHETEN:
 *   * Skydekke er avgjørende. Tett skydekke demper månelyset 1-2 dekader og
 *     kan snu 'utmerket' til 'ingen'. Hent faktisk skydekke fra MET-modulen og
 *     send det inn — og erstatt T_sky med en ordentlig strålingsmodell når den
 *     finnes. Delvis skydekke er verre enn tallene antyder fordi det varierer
 *     i tid og rom.
 *   * Snødekke kan doble-tredoble effektiv scenelysstyrke (albedo 0.8 mot 0.1
 *     for barskog). Vått, mørkt løvterreng er verst.
 *   * Terrengskygge (se `shadow.ts`), skog, tåke, dis og røyk regnes ikke.
 *   * Lysforurensning fra byer/anlegg kan dominere fullstendig nær Oslo, og
 *     virker begge veier: mer lys til NVG, men også blooming og tapt
 *     mørkeadapsjon.
 *   * Ingen modell for NVG-modell/generasjon, tilstand på røret, eller
 *     operatørens erfaring.
 * Bruk klassifiseringen som planleggingsindikator, ikke som en garanti.
 */
export function nvgAssessment(timeMs, lat, lon, opts = {}) {
    const m = moonPosition(timeMs, lat, lon);
    const ph = moonPhase(timeMs);
    const sun = sunPosition(timeMs, lat, lon);
    const phase = lightPhaseAt(timeMs, lat, lon);
    const cloud = opts.cloudCoverFraction === undefined
        ? null
        : Math.max(0, Math.min(1, opts.cloudCoverFraction));
    const notes = [];
    // Månen under horisonten ⇒ null månelys, uansett hvor full den er.
    let moonlightMilliLux = 0;
    if (m.elevationDeg > 0) {
        const tAtm = 10 ** (-0.4 * EXTINCTION_MAG_PER_AIRMASS * (airmass(m.apparentElevationDeg) - 1));
        moonlightMilliLux =
            FULL_MOON_ZENITH_MLX *
                moonPhaseBrightnessFactor(ph.phaseAngleDeg) *
                Math.sin(m.elevationDeg * DEG) *
                tAtm;
    }
    else {
        notes.push(`Månen er under horisonten (elevasjon ${m.elevationDeg.toFixed(1)}°) — ingen månelys, ` +
            `uansett at belyst andel er ${(ph.illuminatedFraction * 100).toFixed(0)} %.`);
    }
    let ambientMilliLux = AMBIENT_CLEAR_MLX;
    if (cloud !== null) {
        const tSky = 10 ** (-2 * cloud * cloud);
        moonlightMilliLux *= tSky;
        ambientMilliLux = AMBIENT_CLEAR_MLX + (AMBIENT_OVERCAST_MLX - AMBIENT_CLEAR_MLX) * cloud;
        notes.push(`Skydekke ${(cloud * 100).toFixed(0)} % gir dempningsfaktor ${tSky.toFixed(3)} på månelyset ` +
            `(grov modell — erstatt med MET-basert strålingsmodell).`);
    }
    else {
        notes.push('Ingen skydekke oppgitt: regnet som KLAR HIMMEL. Det er det optimistiske ' +
            'tilfellet. Hent skydekke fra MET-værmodulen før dette brukes i en ordre.');
    }
    if (sun.elevationDeg > -0.833) {
        notes.push('Sola er over horisonten — dagslys, NVG er ikke relevant.');
    }
    else if (sun.elevationDeg > -12) {
        notes.push(`Sola står ${sun.elevationDeg.toFixed(1)}° under horisonten: det er ennå ikke nautisk ` +
            'mørke (BMNT/EENT-terskelen −12°). Restlys fra sola dominerer over månelyset.');
    }
    notes.push('Snødekke, terrengskygge, skog og lysforurensning er IKKE med i modellen; ' +
        'de kan endre effektiv scenelysstyrke med en faktor 3 eller mer i begge retninger.');
    const totalMilliLux = moonlightMilliLux + ambientMilliLux;
    return {
        moonElevationDeg: m.elevationDeg,
        moonAzimuthDeg: m.azimuthDeg,
        illuminatedFraction: ph.illuminatedFraction,
        phaseName: ph.phaseName,
        phaseAngleDeg: ph.phaseAngleDeg,
        sunElevationDeg: sun.elevationDeg,
        lightPhase: phase,
        moonlightMilliLux,
        moonlightIndex: Math.min(1, moonlightMilliLux / FULL_MOON_ZENITH_MLX),
        ambientMilliLux,
        totalMilliLux,
        level: classify(totalMilliLux),
        cloudCoverFraction: cloud,
        notes,
    };
}
//# sourceMappingURL=moon.js.map